import React, { useEffect, useState } from "react";
import { Bell, Check, ChevronRight, X, Building, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import { Card, CardContent } from "../../../components/ui/card";
import { ScrollArea } from "../../../components/ui/scroll-area";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../../components/ui/avatar";
import { Badge } from "../../../components/ui/badge";
import { AlertCircle } from "lucide-react";
import { InvitationInfo, TenantInfo } from "./interface";
import { useInvitationManagement } from "../../Services/InvitationManagement";
import { useTenantManagement } from "../../Services/TenantManagement";
import CommonModal from "../Modals/CommonModal";
import CompanyRegistration from "../CompanyProfile/CompanyProfile";
import { useUIContext } from "../../../Contexts/UIProvider";
import { useUserContext } from "../../../Contexts/UserProvider";
import { RegistrationStatus } from "../../../Utilities/enums";
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse";
import { toast } from "react-toastify";
import { CirclePlus, Sheet as SheetIcon, Building2 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "../../../components/ui/sheet"

const InvitationManagement = (props: any) => {
  const [invitations, setInvitations] = useState<InvitationInfo[]>([]);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [isRegisterTenant, setIsRegisterTenant] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    selectedInvitations: null,
    selectedCompany: null,
    currentView: "invitations",
  });

  const {
    getInvitations,
    tenantAcceptInvitation,
    userAcceptInvitation,
    declineInvitation,
    readInvitation,
  } = useInvitationManagement();
  const { getTenantsForUsers } = useTenantManagement();
  const { user } = useUserContext();
  const { notifyAccountDashboardRefresh } = useUIContext();

  useEffect(() => {
    if (props?.isInvitation) {
      getAllInvitations();
      getAllTenants();
    }
  }, [props?.isInvitation]);

  useEffect(() => {
    if (props?.isInvitation && props?.currentView) {
      console.log("Values : ", props);
      setSelectedItem({
        selectedInvitations: props?.selectedInvitations ? props?.selectedInvitations : null,
        selectedCompany: null,
        currentView: props?.currentView || "invitations",
      });
    }
  }, [props]);

  const getAllInvitations = async () => {
    if (user?.email) {
      const response: any = await getInvitations(user?.email);
      const mappedResponse: InvitationInfo[] = response?.map(
        (invitations: any) => ({
          id: invitations.id,
          tenantType: invitations.userType,
          invitedTo: invitations.invitedTo,
          invitedUserFrom: invitations.invitedFromUserName,
          invitedTenantFrom: invitations.invitedFromTenantName,
          invitationCreatedDate: invitations.createdDate,
          logo: invitations.logo,
        })
      );
      setInvitations(mappedResponse);
    }
  };

  const getAllTenants = async () => {
    if (user?.email) {
      const response: any = await getTenantsForUsers();
      const mappedResponse: TenantInfo[] = response?.map(
        (invitations: any) => ({
          id: invitations.id,
          companyName: invitations.name,
          code: invitations.code,
          description: invitations.description,
          address: invitations.address,
          registrationStatus: invitations.registrationStatus,
        })
      );
      setTenants(mappedResponse);
    }
  };

  const handlChange = (type: string, value: any, view: string) => {
    setSelectedItem((prev) => ({
      ...prev,
      [type]: value,
      currentView: view,
    }));
  };

  const handleCancel = () => {
    props?.setIsInvitation?.(false);
    resetState();
  };

  const resetState = () => {
    props?.setIsInvitation?.(false);
    setIsConfirming(false);
    setIsDeclining(false);
    setSelectedItem({
      selectedInvitations: null,
      selectedCompany: null,
      currentView: "invitations",
    });
  };

  const handleCreateNewTenant = () => {
    setIsRegisterTenant(true);
    props?.setIsInvitation?.(false);
  };

  const handleRegisterTenantOpenChange = (open: boolean) => {
    setIsRegisterTenant(open);
    if (!open && !props?.isInvitation) {
      props?.setIsInvitation?.(true);
    }
  };

  const handleCompanyRegistrationSuccess = () => {
    getAllTenants();
    props?.onSuccessCallBack?.();
  };

  const isPendingApproval = (registrationStatus?: number) =>
    registrationStatus === RegistrationStatus.Pending;

  const handleConfirm = async (type: string) => {
    if (isConfirming || isDeclining) return;

    if (
      type === "confirmation" &&
      isPendingApproval(selectedItem?.selectedCompany?.registrationStatus)
    ) {
      toast.error("This company is not yet approved and cannot be attached.");
      return;
    }

    setIsConfirming(true);
    try {
      const invitationId = selectedItem?.selectedInvitations?.id;
      let response;

      if (type === "user_comfirmation") {
        response = await userAcceptInvitation(invitationId);
      } else {
        response = await tenantAcceptInvitation(
          invitationId,
          null,
          user?.email,
          selectedItem?.selectedCompany?.code
        );
      }

      if (isApiError(response)) {
        toast.error(
          getApiErrorMessage(response) || "Failed to accept the invitation. Please try again."
        );
        return;
      }

      toast.success("Invitation accepted successfully!");
      props?.setIsInvitation?.(false);
      props?.setIsDialogOpen?.(false);
      props?.onSuccessCallBack?.();
      notifyAccountDashboardRefresh();
      resetState();
    } catch (err) {
      toast.error(
        getApiErrorMessage(err) || "Failed to accept the invitation. Please try again."
      );
      console.log("Accept Response : ", err);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDecline = async () => {
    if (isConfirming || isDeclining) return;

    setIsDeclining(true);
    try {
      const response = await declineInvitation(selectedItem?.selectedInvitations?.id);

      if (isApiError(response)) {
        toast.error(
          getApiErrorMessage(response) || "Failed to decline the invitation. Please try again."
        );
        return;
      }

      getAllInvitations();
      setSelectedItem({
        selectedInvitations: null,
        selectedCompany: null,
        currentView: "invitations",
      });
    } catch (err) {
      toast.error(
        getApiErrorMessage(err) || "Failed to decline the invitation. Please try again."
      );
      console.log("Delete Error : ", err);
    } finally {
      setIsDeclining(false);
    }
  };

  const handleReadInvitation = (invitationId: string) => {
    readInvitation(invitationId)
      .then((res) => {
        console.log("Read  : ", res);
      })
      .catch((err) => {
        console.log("Read Error : ", err);
      });
  };

  const getRoleBadgeColor = (tenantType: string) => {
    switch (tenantType) {
      case "ReSeller":
        return "bg-blue-50 text-blue-700 border border-blue-200"
      case "Partner":
        return "bg-purple-50 text-purple-700 border border-purple-200"
      case "Admin":
        return "bg-emerald-50 text-emerald-700 border border-emerald-200"
      case "SuperAdmin":
        return "bg-rose-50 text-rose-700 border border-rose-200"
      case "User":
        return "bg-amber-50 text-amber-700 border border-amber-200"
      case "Guest":
        return "bg-cyan-50 text-cyan-700 border border-cyan-200"
      default:
        return "bg-slate-50 text-slate-700 border border-slate-200"
    }
  }

  const isActionLoading = isConfirming || isDeclining;

  return (
    <div className="">
      <Sheet
        open={props.isInvitation}
        onOpenChange={(open) => {
          props.setIsInvitation(open);
          if (!open) resetState();
        }}
      >
        <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto">
          <ScrollArea className="max-h-[900px]">
            <SheetHeader className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-purple-100 rounded-lg">
                    <SheetIcon className="w-6 h-6 bg-background text-purple-600" />
                  </div>
                  <div>
                    <SheetTitle className="text-xl font-bold">
                      {selectedItem?.currentView === "invitations" &&
                        "Your Invitations"}
                      {selectedItem?.currentView === "companies" &&
                        "Respond to Partnership Invitation"}
                      {selectedItem?.currentView === "confirmation" &&
                        "Confirm Attachment"}
                      {selectedItem?.currentView === "user_comfirmation" &&
                        "Confirm User Invitation"}
                    </SheetTitle>
                    <p className="text-sm text-gray-300 mt-1">
                      {selectedItem?.currentView === "invitations" &&
                        "Select an invitation to proceed."}
                      {/* {selectedItem?.currentView === "companies" &&
                  "Select an existing company or create a new one to proceed."} */}
                      {selectedItem?.currentView === "confirmation" &&
                        "Please review the details before confirming."}
                      {selectedItem?.currentView === "user_comfirmation" &&
                        "Please review the details before confirming."}
                    </p>
                  </div>
                </div>
              </div>
            </SheetHeader>
            {selectedItem?.currentView === "invitations" && (
              <ScrollArea className="h-[800px]">
                {invitations.length > 0 ? (
                  invitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="group relative flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 m-4 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-slate-300 cursor-pointer"
                      onClick={() => {
                        handleReadInvitation(invitation.id);
                        handlChange(
                          "selectedInvitations",
                          invitation,
                          invitation?.tenantType === "ReSeller" ||
                            invitation?.tenantType === "Partner"
                            ? "companies"
                            : "user_comfirmation"
                        );
                      }}
                    >
                      {/* Avatar and Company Info */}
                      <div className="flex items-center gap-4 flex-1">
                        <Avatar className="h-14 w-14 ring-2 ring-slate-100">
                          <AvatarImage src={invitation?.logo || "/placeholder.svg"} alt={invitation?.invitedTenantFrom} />
                          <AvatarFallback className="text-white font-semibold">
                            {invitation?.invitedTenantFrom?.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex flex-col gap-2">
                          <h3 className="font-semibold text-base leading-tight">{invitation?.invitedTenantFrom}</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-300 font-medium">Join us as</span>
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(invitation?.tenantType)}`}
                            >
                              {invitation?.tenantType}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="hidden sm:block mx-6 h-16 bg-slate-200 w-px" />

                      {/* Invited By Info */}
                      <div className="hidden sm:flex flex-col items-center gap-1 min-w-max">
                        <p className="text-xs text-slate-300 font-medium">Invited by</p>
                        <p className="text-sm font-medium text-center">{invitation?.invitedUserFrom}</p>
                      </div>

                      {/* Chevron Icon */}
                      <ChevronRight className="ml-4 h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-slate-600" />
                    </div>

                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                    <p className="text-sm">You don’t have any invitations right now</p>
                    <p className="text-xs">You’ll be notified here when a new invitation arrives</p>
                  </div>
                )}
              </ScrollArea>

            )}

            {selectedItem?.currentView === "companies" && (
              <div className="space-y-3 m-4">
                <Card className="shadow-none card-explicit">
                  <CardContent className="flex flex-col gap-2 items-center p-2 text-center">
                    <Avatar className="w-24 h-24">
                      <AvatarImage className="w-24 h-24 bg-green-400"
                        src={selectedItem.selectedInvitations?.logo}
                        alt={
                          selectedItem.selectedInvitations?.invitedTenantFrom
                        }
                      />
                      <AvatarFallback className="w-24 h-24 bg-yellow-300 text-3xl">
                        {selectedItem.selectedInvitations?.invitedTenantFrom?.substring(
                          0,
                          2
                        )}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex flex-col gap-2">
                      <h3 className="font-semibold text-3xl">
                        {selectedItem?.selectedInvitations?.invitedTenantFrom}
                      </h3>
                      <p className="text-lg text-muted-foreground">
                        {selectedItem?.selectedInvitations?.invitedUserFrom} {" "} has invited you to join as a{" "}
                        {selectedItem?.selectedInvitations?.tenantType} for their {" "}
                        {selectedItem?.selectedInvitations?.invitedTo}{" "}
                        Organization
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex w-full text-base pt-2">
                  Select a company that matches the name requested by
                  {selectedItem?.selectedInvitations?.companyName}, or create a new one if needed.
                </div>

                <>
                  <div className="flex items-end h-full">
                    <h3 className="font-semibold pt-0 flex-grow">Your current companies</h3>
                    <Button
                      className="bg-primary"
                      onClick={handleCreateNewTenant}
                    >
                      <CirclePlus />
                      Create new company
                    </Button>
                  </div>

                  <ScrollArea className="h-[580px]">
                    {tenants.length > 0 ? (
                      <div className="space-y-3">
                        {tenants.map((company) => {
                          const pendingApproval = isPendingApproval(company.registrationStatus);

                          return (
                          <Card key={company.id} className="hover:shadow-md transition-all duration-200">
                            <CardContent className="flex items-center p-4">
                              <Avatar className="mr-3 h-10 w-10">
                                <AvatarImage
                                  // src={company?.logo}
                                  alt={company?.companyName}
                                />
                                <AvatarFallback>
                                  {company?.companyName?.substring(0, 2)}
                                </AvatarFallback>
                              </Avatar>

                              <div className="flex-grow min-w-0">
                                <h3 className="font-medium truncate">
                                  {company?.companyName}
                                </h3>
                                <p className="text-sm text-gray-300 truncate">
                                  {company?.address || "No address provided"}
                                </p>
                                {pendingApproval && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    This company is not yet approved.
                                  </p>
                                )}
                              </div>

                              <Button
                                className="ml-3 w-32"
                                variant="outline"
                                disabled={pendingApproval}
                                onClick={() =>
                                  handlChange("selectedCompany", company, "confirmation")
                                }
                              >
                                Select
                              </Button>
                            </CardContent>
                          </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[460px] text-gray-500 space-y-3">
                        <p className="text-base font-medium text-gray-700">
                          No companies found
                        </p>
                        <p className="text-sm text-gray-500 text-center max-w-xs">
                          Looks like you haven’t created a company yet.
                          Create one now to get started.
                        </p>
                        <Button
                          className="bg-primary"
                          onClick={handleCreateNewTenant}
                        >
                          <CirclePlus />
                          Create new company
                        </Button>
                      </div>
                    )}
                  </ScrollArea>

                </>
              </div>
            )}


            {selectedItem?.currentView === "user_comfirmation" && (
              <div className="m-4 h-[200px]">
                <div className="flex items-center space-x-4 mb-4">
                  <Avatar>
                    <AvatarImage
                      src={selectedItem?.selectedInvitations?.logo}
                      alt={selectedItem?.selectedInvitations?.invitedTenantFrom}
                    />
                    <AvatarFallback>
                      {selectedItem?.selectedInvitations?.invitedTenantFrom?.substring(
                        0,
                        2
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-medium">
                      {selectedItem?.selectedInvitations?.invitedTenantFrom}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedItem?.selectedInvitations?.tenantType}
                    </p>
                    <Badge variant="secondary">
                      Invited on{" "}
                      {new Date(
                        selectedItem?.selectedInvitations?.invitationCreatedDate
                      ).toLocaleDateString()}
                    </Badge>
                  </div>
                </div>

                <p className="text-base text-gray-300">
                  By confirming, you agree to join us as a{" "}
                  {selectedItem?.selectedInvitations?.tenantType}, the invitation
                  from {selectedItem?.selectedInvitations?.invitedTenantFrom}
                </p>
              </div>
            )}

            {selectedItem?.currentView === "confirmation" && (
              <div className="space-y-4 m-4">
                <div className="flex justify-between">
                  <div className="flex items-center space-x-4 mb-4">
                    <Avatar>
                      <AvatarImage
                        src={selectedItem?.selectedInvitations?.logo}
                        alt={selectedItem?.selectedInvitations?.invitedTenantFrom}
                      />
                      <AvatarFallback>
                        {selectedItem.selectedInvitations?.invitedTenantFrom?.substring(
                          0,
                          2
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-medium">
                        {selectedItem?.selectedInvitations?.invitedTenantFrom}
                      </h3>
                      <p className="text-sm text-gray-300">
                        {selectedItem?.selectedInvitations?.tenantType}
                      </p>
                      <Badge variant="secondary">
                        Invited on{" "}
                        {new Date(
                          selectedItem?.selectedInvitations?.invitationCreatedDate
                        ).toLocaleDateString()}
                      </Badge>
                    </div>
                  </div>

                  <div className="bg-white pr -4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Building className="h-5 w-5 text-gray-500" />
                      <div>
                        <h4 className="font-medium">Attaching :</h4>
                        <p>{selectedItem?.selectedCompany?.companyName}</p>
                        <p className="text-sm text-gray-500">
                          {selectedItem?.selectedCompany?.address}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-base text-gray-300">
                  By confirming, you agree to attach your company "
                  {selectedItem?.selectedCompany?.companyName}" to the invitation
                  from {selectedItem?.selectedInvitations?.invitedTenantFrom} as
                  a {selectedItem?.selectedInvitations?.tenantType}.
                </p>
              </div>
            )}


          </ScrollArea>

          <SheetFooter className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3">
            <div className="flex">
              {selectedItem.currentView !== "companies" && (
                <>
                  <Button className="mr-4" variant="outline" onClick={handleCancel} disabled={isActionLoading}>
                    <X className="mr-2 h-4 w-4" />
                    Cancel
                  </Button>
                </>
              )}

              {selectedItem.currentView === "companies" && (
                <>
                  <Button
                    className="mr-4"
                    variant="outline"
                    disabled={isActionLoading}
                    onClick={() => handlChange(
                      "selectedInvitations",
                      null,
                      "invitations"
                    )}>
                    <X className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                </>
              )}


              {selectedItem.currentView !== "invitations" && (
                <>
                  <Button
                    className="flex-grow mr-4"
                    variant="destructive"
                    onClick={handleDecline}
                    disabled={isActionLoading}
                  >
                    {isDeclining ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Declining...
                      </>
                    ) : (
                      "Decline request"
                    )}
                  </Button>
                </>
              )}

              {selectedItem.currentView === "confirmation" && (
                <Button
                  onClick={() => handleConfirm("confirmation")}
                  className="flex-grow mr-4"
                  disabled={isActionLoading}
                >
                  {isConfirming ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Confirm request
                    </>
                  )}
                </Button>
              )}

              {selectedItem.currentView === "user_comfirmation" && (
                <Button
                  onClick={() => handleConfirm("user_comfirmation")}
                  className="mr-4"
                  disabled={isActionLoading}
                >
                  {isConfirming ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Confirm
                    </>
                  )}
                </Button>
              )}
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <CompanyRegistration
        open={isRegisterTenant}
        onOpenChange={handleRegisterTenantOpenChange}
        setIsContentLoad={() => {}}
        setIsShow={setIsRegisterTenant}
        setIsDialogOpen={props?.setIsInvitation}
        setIsRegisterTenant={setIsRegisterTenant}
        onSuccessCallBack={handleCompanyRegistrationSuccess}
      />

      {/* <CommonModal
        isShow={isRegisterTenant}
        setIsDialogOpen={setIsRegisterTenant}
        content={
          
        }
        size={"5xl"}
      ></CommonModal> */}
    </div>
  );
};

export default InvitationManagement;
